import { IR } from 'typecheck.macro';

/**
 * Parses an unknown value to a static type by checking its shape at runtime
 * @param value - An unknown value to check
 * @param validator - A validator for a particular type
 * @returns the input value with correct typing if it matched the correct shape
 * @throws when the input is an incorrect shape
 */
export const parse = <T>(value: unknown, validator: Validator<T>): T => {
  const errors: ValidatorErrors = [];

  if (validator(value, errors)) {
    return value;
  } else {
    const firstError = errors[0];

    throw firstError
      ? new Error(
          `Wrong value at ${firstError[0]}: saw ${JSON.stringify(firstError[1])}, expected ${
            firstError[2]
          }`,
        )
      : new Error('Failed to validate');
  }
};

/**
 * Parses a JSON string to a static type by checking its shape at runtime
 * @param value - A JSON string
 * @param validator - A validator for a particular type
 * @returns the parsed value with correct typing if it matched the correct shape
 * @throws when the input is invalid JSON or an incorrect shape
 */
export const parseFromJsonString = <T>(value: string, validator: Validator<T>): T =>
  parse(JSON.parse(value), validator);

export const parseFromFetchResponse = async <T>(
  response: Response,
  validator: Validator<T>,
): Promise<T> => parse(await response.json(), validator);

type ValidatorErrors = Array<[string, unknown, IR.IR | string]>;

/** The type returned from a call to `createValidator` or `createDetailedValidator`
 * in the `typecheck.macro` package. These functions are macros which generate validation
 * code at compile-time.
 *
 * Do not implement this interface directly. */
export type Validator<T> = (value: unknown, errs: ValidatorErrors) => value is T;
